import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { apiError } from "@/lib/oraya-api";
import { useAuth } from "@/context/AuthContext";
import { OrayaMark, Seo } from "@/components/site/primitives";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") { toast.error("This account does not have admin access."); return; }
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const input = "w-full bg-transparent border-b border-white/20 focus:border-oraya-accent focus:outline-none py-3 text-oraya-mist placeholder:text-oraya-slate/50 transition-colors";

  return (
    <div data-theme="dark" className="min-h-screen bg-oraya-black flex items-center justify-center px-6">
      <Seo title="Admin — ORAYA" description="ORAYA admin." />
      <div className="grain" />
      <div className="w-full max-w-md relative z-10">
        <div className="flex flex-col items-center mb-10">
          <OrayaMark size={44} />
          <span className="display text-2xl font-medium tracking-[0.28em] text-oraya-mist mt-4">ORAYA</span>
          <span className="text-xs uppercase tracking-[0.25em] text-oraya-slate mt-2">Admin</span>
        </div>

        <div className="glass-heavy rounded-3xl p-8">
          {location.state?.denied && <p className="text-sm text-red-400 mb-4">That account is not authorized for admin access.</p>}
          <form onSubmit={submit} className="flex flex-col gap-5" data-testid="admin-login-form">
            <input className={input} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" required />
            <input className={input} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" required />
            <button type="submit" disabled={busy} data-testid="admin-login-submit" className="mt-2 rounded-full px-6 py-3 bg-oraya-mist text-oraya-black font-medium hover:bg-white transition-colors disabled:opacity-50">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-oraya-slate">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <button onClick={google} data-testid="admin-google-login" className="w-full rounded-full px-6 py-3 bg-white text-oraya-black font-medium hover:bg-oraya-mist transition-colors flex items-center justify-center gap-3">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Continue with Google
          </button>
        </div>
      </div>
    </div>
  );
}
